import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedInlineText } from './formatted-inline-text';
import {
  DEFAULT_RECORD_CARD_CONTINUE_TEXT,
} from '../systems/record-card-content';
import type { RecordCardCaseCost, RecordCardKeyFigure, RecordCardReflex, RecordCardScene } from '../types';
import { isMotionReduced } from '../systems/accessibility-settings';
import styles from './attack-card-screen.module.css';

export const DEFAULT_ATTACK_CARD_NEXT_TEXT = 'Suivant →';

export interface AttackCardScreenProps {
  title: string;
  mission?: number;
  imageSrc?: string;
  moment?: string;
  serialSerie?: string;
  serialNumber?: string | null;
  symptoms?: string;
  target?: string;
  keyFigure?: RecordCardKeyFigure;
  reflex?: RecordCardReflex;
  caseName?: string;
  caseWhen?: string;
  caseTitle?: string;
  caseSteps?: string[];
  caseCosts?: RecordCardCaseCost[];
  continueText?: string;
  nextHint?: string;
  background?: string;
  collectionEarned?: number;
  collectionTotal?: number;
  /** Depuis l'inventaire : carte à plat, panneau déjà déroulé, pas d'animation. */
  instant?: boolean;
  onContinue: () => void;
}

export interface AttackCardSceneMapperOptions {
  interpolate: (text: string) => string;
  resolveAsset: (path: string | undefined) => string | undefined;
  background?: string;
  instant?: boolean;
  onContinue: () => void;
}

/** Map a recordCard attaque scene to AttackCardScreen props. */
export function mapAttackCardSceneToProps(
  scene: RecordCardScene,
  options: AttackCardSceneMapperOptions,
): AttackCardScreenProps {
  const { interpolate, resolveAsset, background, instant, onContinue } = options;
  const serialDisplay = scene.serial
    ? `${String(scene.serial.n).padStart(2, '0')} / ${scene.serial.total}`
    : null;

  return {
    title: interpolate(scene.title),
    mission: scene.mission,
    imageSrc: resolveAsset(scene.image),
    moment: scene.moment ? interpolate(scene.moment) : undefined,
    serialSerie: scene.serial?.serie,
    serialNumber: serialDisplay,
    symptoms: scene.symptoms ? interpolate(scene.symptoms) : undefined,
    target: scene.target ? interpolate(scene.target) : undefined,
    keyFigure: scene.keyFigure
      ? {
          n: interpolate(scene.keyFigure.n),
          label: interpolate(scene.keyFigure.label),
        }
      : undefined,
    reflex: scene.reflex
      ? {
          text: interpolate(scene.reflex.text),
          actions: scene.reflex.actions?.map((action) => interpolate(action)),
        }
      : undefined,
    caseName: scene.case?.name ? interpolate(scene.case.name) : undefined,
    caseWhen: scene.case?.when ? interpolate(scene.case.when) : undefined,
    caseTitle: scene.case?.title ? interpolate(scene.case.title) : undefined,
    caseSteps: scene.case?.steps?.map((step) => interpolate(step)),
    caseCosts: scene.case?.cost?.map((cost) => ({
      n: interpolate(cost.n),
      label: interpolate(cost.label),
    })),
    continueText: scene.continueText,
    nextHint: scene.nextHint ? interpolate(scene.nextHint) : undefined,
    background,
    collectionEarned: scene.serial?.n,
    collectionTotal: scene.serial?.total,
    instant,
    onContinue,
  };
}

function CardCorners() {
  return (
    <>
      <span className={`${styles.corner} ${styles.cornerHg}`} aria-hidden />
      <span className={`${styles.corner} ${styles.cornerHd}`} aria-hidden />
      <span className={`${styles.corner} ${styles.cornerBg}`} aria-hidden />
      <span className={`${styles.corner} ${styles.cornerBd}`} aria-hidden />
    </>
  );
}

export const AttackCardScreen = memo(function AttackCardScreen({
  title,
  mission,
  imageSrc,
  moment,
  serialSerie = 'CARTE ATTAQUE',
  serialNumber,
  symptoms,
  target,
  keyFigure,
  reflex,
  caseName,
  caseWhen,
  caseTitle,
  caseSteps = [],
  caseCosts = [],
  continueText = DEFAULT_RECORD_CARD_CONTINUE_TEXT,
  nextHint,
  background,
  collectionEarned = 0,
  collectionTotal = 6,
  instant: instantProp,
  onContinue,
}: AttackCardScreenProps) {
  const [deployed, setDeployed] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [reducedMotion] = useState(() => isMotionReduced());
  const deployTimeRef = useRef(0);

  const instant = instantProp ?? reducedMotion;

  useEffect(() => {
    if (instant) {
      setDeployed(true);
      setPressed(true);
    } else {
      setDeployed(false);
      setPressed(false);
    }
  }, [title, background, instant]);

  const steps = caseSteps.slice(0, 3);
  const costs = caseCosts.slice(0, 3);
  const gestures = (reflex?.actions ?? []).slice(0, 3);
  const total = Math.max(1, collectionTotal);
  const earned = Math.min(total, Math.max(0, collectionEarned));

  const handleOverlayClick = useCallback(() => {
    if (!deployed) return;
    if (!pressed) {
      setPressed(true);
    }
  }, [deployed, pressed]);

  const handleContinueClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (!deployed) {
        deployTimeRef.current = performance.now();
        setDeployed(true);
        return;
      }

      if (!pressed && performance.now() - deployTimeRef.current < 2000) {
        setPressed(true);
        return;
      }

      onContinue();
    },
    [deployed, pressed, onContinue],
  );

  const rootClass = [
    styles.overlay,
    instant ? styles.instant : styles.play,
    deployed ? styles.deployed : '',
    pressed ? styles.pressed : '',
  ]
    .filter(Boolean)
    .join(' ');

  const buttonLabel = deployed ? continueText : DEFAULT_ATTACK_CARD_NEXT_TEXT;

  return (
    <div
      className={rootClass}
      role="dialog"
      aria-modal="true"
      aria-labelledby="attack-card-title"
      onClick={handleOverlayClick}
    >
      {background ? (
        <div
          className={styles.background}
          style={{ backgroundImage: `url(${background})` }}
          aria-hidden
        />
      ) : null}
      <div className={styles.veil} aria-hidden />
      <div className={styles.bandTop} aria-hidden />
      <div className={styles.bandBottom} aria-hidden />

      <div className={styles.deck} aria-hidden>
        <div className={styles.deckCard}>
          <div className={styles.deckCardInner} />
        </div>
        <div className={styles.deckCard}>
          <div className={styles.deckCardInner} />
        </div>
        <div className={styles.deckCard}>
          <div className={styles.deckCardInner} />
        </div>
      </div>

      <div className={styles.cardColumn}>
        <div className={styles.card}>
          <div className={styles.cardFace}>
            <CardCorners />
            <div className={styles.serie}>
              <span className={styles.serieName}>{serialSerie}</span>
              {serialNumber ? <span className={styles.serieNum}>{serialNumber}</span> : null}
            </div>
            <h1 id="attack-card-title" className={styles.cardTitle}>
              <FormattedInlineText text={title} />
            </h1>
            {mission !== undefined ? (
              <div className={styles.missionLine}>RENCONTRÉ · MISSION {mission}</div>
            ) : null}
            <div className={styles.art}>
              {imageSrc ? (
                <img src={imageSrc} alt="" />
              ) : (
                <div className={styles.artPlaceholder}>?</div>
              )}
              {moment ? (
                <div className={styles.artBanner}>{moment.toUpperCase()}</div>
              ) : null}
            </div>
            <div className={styles.textBlocks}>
              {symptoms ? (
                <div>
                  <div className={styles.blockLabel}>SYMPTÔMES</div>
                  <div className={styles.blockText}>
                    <FormattedInlineText text={symptoms} />
                  </div>
                </div>
              ) : null}
              {target ? (
                <div>
                  <div className={styles.blockLabel}>CIBLE</div>
                  <div className={styles.blockText}>
                    <FormattedInlineText text={target} />
                  </div>
                </div>
              ) : null}
              {reflex?.text ? (
                <div>
                  <div className={styles.blockLabel}>LE RÉFLEXE</div>
                  <div className={styles.reflexText}>
                    <FormattedInlineText text={reflex.text} />
                  </div>
                </div>
              ) : null}
            </div>
            <div className={styles.stats}>
              {keyFigure ? (
                <>
                  <span className={styles.statsFigure}>
                    <FormattedInlineText text={keyFigure.n} />
                  </span>
                  <span className={styles.statsLegend}>
                    <FormattedInlineText text={keyFigure.label} />
                  </span>
                </>
              ) : target ? (
                <span className={styles.statsLegend}>
                  <FormattedInlineText text={target} />
                </span>
              ) : null}
            </div>
          </div>
          <div className={styles.cardBack} aria-hidden>
            <div className={styles.cardBackSeal}>?</div>
            <div className={styles.cardBackSerie}>{serialSerie}</div>
          </div>
        </div>

        <div className={styles.gauge}>
          <span className={styles.gaugeLabel}>COLLECTION</span>
          <div className={styles.gaugeTicks}>
            {Array.from({ length: total }, (_, index) => (
              <span
                key={index}
                className={[styles.gaugeTick, index < earned ? styles.gaugeTickOk : ''].filter(Boolean).join(' ')}
              />
            ))}
          </div>
          <span className={styles.gaugeCount}>
            {earned} / {total}
          </span>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelRub}>C&apos;EST DÉJÀ ARRIVÉ</div>
        {caseTitle ? (
          <h2 className={styles.panelTitle}>
            <FormattedInlineText text={caseTitle} />
          </h2>
        ) : null}
        {(caseName || caseWhen) ? (
          <div className={styles.panelWhen}>
            {caseName ? (
              <b>
                <FormattedInlineText text={caseName} />
              </b>
            ) : null}
            {caseName && caseWhen ? ' · ' : null}
            {caseWhen ? <FormattedInlineText text={caseWhen} /> : null}
          </div>
        ) : null}
        {steps.length > 0 ? (
          <div className={styles.steps}>
            {steps.map((step, index) => (
              <div key={step} className={styles.step}>
                <span className={styles.stepNum}>{index + 1}</span>
                <span>
                  <FormattedInlineText text={step} />
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {costs.length > 0 ? (
          <div
            className={styles.costs}
            style={{ gridTemplateColumns: `repeat(${costs.length}, minmax(0, 1fr))` }}
          >
            {costs.map((cost) => (
              <div key={`${cost.n}-${cost.label}`} className={styles.cost}>
                <b>
                  <FormattedInlineText text={cost.n} />
                </b>
                <span>
                  <FormattedInlineText text={cost.label} />
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {gestures.length > 0 ? (
          <div className={styles.divider}>
            <div className={styles.panelRub}>LES GESTES QUI L&apos;ARRÊTENT</div>
            <div className={styles.gestures}>
              {gestures.map((gesture) => (
                <div key={gesture} className={styles.gesture}>
                  <span className={styles.gestureCheck}>✓</span>
                  <span>
                    <FormattedInlineText text={gesture} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className={styles.foot}>
        <button
          type="button"
          className={styles.continueBtn}
          onClick={handleContinueClick}
        >
          {buttonLabel}
        </button>
        {deployed && nextHint ? (
          <div className={styles.nextHint}>{nextHint}</div>
        ) : null}
      </div>
    </div>
  );
});
